/**
 * Release sections (dashboard, nav badges, calendar nudge, Brand Portfolio inbox)
 *
 * Mirrors the backend contract (DASHBOARD-NAV-PLAN.md section 1.6):
 *   GET /api/teams/:teamId/release-sections?limit=
 *   GET /api/teams/:teamId/release-badges
 *   GET /api/teams/:teamId/release-sections/newly-released?since=
 *   PUT /api/teams/:teamId/surfaces/:surface/seen
 *   GET /api/release/inbox?section=&teamId=&cursor=&limit=
 *   GET /api/release/inbox/counts
 * Keep in sync with backend/src/types/release-sections.types.ts.
 */

import type { BoardItem } from '@/types/releaseBoard';

/** The five sections. Wire names of the inbox `section` query parameter. */
export type ReleaseSectionKey =
  | 'awaiting'
  | 'changes_made'
  | 'released_by_me'
  | 'ready_to_schedule'
  | 'changes_requested';

export const RELEASE_SECTION_KEYS: readonly ReleaseSectionKey[] = [
  'awaiting',
  'changes_made',
  'released_by_me',
  'ready_to_schedule',
  'changes_requested',
];

/** Sections an inbox page can be requested for in the Brand Portfolio. */
export type ReleaseInboxSectionKey = ReleaseSectionKey;

/** Surfaces whose "last seen" stamp the server keeps per person and brand. */
export type ReleaseSurface = 'calendar' | 'dashboard' | 'release_tour';

export type ReleasePlacement = 'released' | 'scheduled' | 'live';

/** What the viewer is allowed to do in the brand (server truth, from getActor). */
export interface ReleaseViewer {
  isReviewer: boolean;
  isCreator: boolean;
}

/** The decision a reviewer made on an item that came back (changes_requested section). */
export interface ReleaseDecision {
  byName: string;
  commentExcerpt: string | null;
  /** ISO */
  decidedAt: string;
}

export interface ReleaseItemBrand {
  id: number;
  name: string;
  logo: string | null;
}

/**
 * One row of any section: the board card plus section facts.
 * `label`, `postId`, `variationId` and `sentBy` are fields of the backend board DTO that the
 * shared frontend `BoardItem` does not declare.
 */
export interface SectionItem extends BoardItem {
  /** Caption first line; empty string when there is no caption (the client shows its fallback) */
  label: string;
  postId: number;
  variationId: number | null;
  sentBy: { id: number; name: string; email: string };
  teamId: number;
  brand: ReleaseItemBrand;
  /** Newer than the viewer's "last seen" stamp for the surface (always false in the Portfolio) */
  isNew: boolean;
  /** The viewer sent this item for review */
  sentByMe: boolean;
  placement?: ReleasePlacement;
  /** ISO, when the item went live (placement = live) */
  liveAt?: string | null;
  decision?: ReleaseDecision;
}

export interface ReleaseSectionPayload {
  total: number;
  items: SectionItem[];
}

/** Sections the server left out do not apply to the viewer's roles. */
export interface ReleaseSectionsBySlot {
  /** Dashboard In review cards, including the viewer's own submissions when they can create. */
  inReview?: ReleaseSectionPayload;
  awaiting?: ReleaseSectionPayload;
  changesMade?: ReleaseSectionPayload;
  releasedByMe?: ReleaseSectionPayload;
  readyToSchedule?: ReleaseSectionPayload;
  changesRequested?: ReleaseSectionPayload;
}

export interface BrandReleaseSections {
  workflowActive: boolean;
  viewer?: ReleaseViewer;
  /** ISO, the server cut-off the sections were computed with (stamp this, never the client clock) */
  asOf?: string;
  /** ISO, the viewer's previous "last seen" stamp for the dashboard */
  seenSince?: string;
  sections?: ReleaseSectionsBySlot;
  /** Operators only: Asset Pipeline counts. */
  counts?: { inReview: number };
}

export interface ReleaseBadges {
  workflowActive: boolean;
  viewer?: ReleaseViewer;
  /** Reviewers: items waiting for their release (own submissions excluded); null when not a reviewer */
  awaitingForYou?: number | null;
  /** Operators: items released since the last calendar visit; null when not an operator */
  calendar?: { newlyReleased: number; readyToSchedule: number } | null;
  /** Whether the viewer has already seen the release tour */
  tourSeen: boolean;
}

export interface NewlyReleasedItem {
  postId: number;
  variationId: number | null;
  /** ISO */
  releasedAt: string;
}

export interface NewlyReleased {
  /** ISO, the lower bound used (clamped by the server to the last 7 days) */
  since: string;
  /** ISO */
  asOf: string;
  total: number;
  items: NewlyReleasedItem[];
}

export interface ReleaseInboxPage {
  items: SectionItem[];
  nextCursor: string | null;
  total: number;
}

export interface ReleaseInboxCounts {
  awaiting: number;
  changesMade: number;
  changesRequested: number;
  readyToSchedule: number;
}

export interface ReleaseInboxPageParams {
  section: ReleaseInboxSectionKey;
  /** Limit the page to one brand (must be inside the viewer's scope, else 404) */
  teamId?: number | null;
  cursor?: string | null;
  limit?: number;
}

/** Stable key of the exact item inside a post: `<postId>:base` or `<postId>:<variationId>`. */
export const releaseItemKey = (postId: number, variationId: number | null): string =>
  `${postId}:${variationId ?? 'base'}`;
